import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, Outlet } from 'react-router-dom';
import Header from './components/Header';
import FloatingChatButton from './components/FloatingChatButton';
import logoImage from './assets/logo.png';
import TeacherLayout from './pages/teacher/TeacherLayout';
import TeacherBasicInfo from './pages/teacher/BasicInfo';
import TeacherStudentManagement from './pages/teacher/StudentManagement';
import TeacherOrders from './pages/teacher/Orders';
import TeacherMessages from './pages/teacher/Messages';
import PaperReviewList from './pages/teacher/PaperReviewList';
import PaperReviewDetail from './pages/teacher/PaperReviewDetail';
import AssignmentPublish from './pages/teacher/AssignmentPublish';
import AssignmentManage from './pages/teacher/AssignmentManage';
import AssignmentGradingPage from './pages/teacher/AssignmentGradingPage';
import TeacherGiftPoints from './pages/teacher/TeacherGiftPoints';

// 所有页面直接 import，不使用 lazy()，避免生成 chunk 文件
// Meoo 平台发布时不会上传 chunk 文件，导致白屏
import Home from './pages/Home';
import CategoryPage from './pages/CategoryPage';
import ExamListPage from './pages/ExamListPage';
import Quiz from './pages/quiz/Quiz';
import WrongQuestionsPage from './pages/WrongQuestionsPage';
import FavoritesPage from './pages/FavoritesPage';
import FavoritesReviewPage from './pages/FavoritesReviewPage';
import WrongQuestionsReviewPage from './pages/WrongQuestionsReviewPage';
import ExamRecordsPage from './pages/ExamRecordsPage';
import Login from './pages/Login';
import Register from './pages/Register';
import UserProfile from './pages/UserProfile';
import ExamPlatform from './pages/ExamPlatform';
import ExamResult from './pages/ExamResult';
import PaymentResult from './pages/PaymentResult';
import HelpCenter from './pages/HelpCenter';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Notice from './pages/Notice';
import PayRedirect from './pages/PayRedirect';
import NoticeDetail from './pages/NoticeDetail';
import DownloadCenter from './pages/DownloadCenter';
import PythonPlayground from './pages/PythonPlayground';
import CppPlayground from './pages/CppPlayground';
import ScratchEditorPage from './pages/ScratchEditorPage';
import AssignmentsPage from './pages/AssignmentsPage';
import PointCenter from './pages/PointCenter';
import PointTransactions from './pages/PointTransactions';
import MyCorrections from './pages/MyCorrections';

import AdminLayout from './pages/admin/AdminLayout';
import AdminPapers from './pages/admin/Papers';
import AdminMembers from './pages/admin/Members';
import OperationLogs from './pages/admin/OperationLogs';
import AdminUsers from './pages/admin/AdminUsers';
import AdminOrders from './pages/admin/StudentOrders';
import PaperEditor from './pages/admin/PaperEditor';
import CustomerService from './pages/admin/CustomerService';
import AdminMessageConfig from './pages/admin/AdminMessageConfig';
import MessageTest from './pages/admin/MessageTest';
import MessageTemplates from './pages/admin/MessageTemplates';
import SendMessage from './pages/admin/SendMessage';
import MessageStatistics from './pages/admin/MessageStatistics';
import AlipayConfig from './pages/admin/AlipayConfig';
import EmailSettings from './pages/admin/EmailSettings';
import SMSSettings from './pages/admin/SMSSettings';
import AISettings from './pages/admin/AISettings';
import DownloadManager from './pages/admin/DownloadManager';
import CategoryManager from './pages/admin/CategoryManager';
import RecycleBin from './pages/admin/RecycleBin';
import StudentOrders from './pages/admin/StudentOrders';
import AdminTeacherOrders from './pages/admin/TeacherOrders';
import PaperUpload from './pages/admin/PaperUpload';
import PendingPapers from './pages/admin/PendingPapers';
import Teachers from './pages/admin/Teachers';
import AdminNotices from './pages/admin/Notices';
import NavItems from './pages/admin/NavItems';
import FooterSettings from './pages/admin/FooterSettings';
import ScratchAssetsUpload from './pages/admin/ScratchAssetsUpload';
import AnswerSettings from './pages/admin/AnswerSettings';
import HomepageEditor from './pages/admin/HomepageEditor';
import PointSettings from './pages/admin/PointSettings';
import ErrorCorrectionReview from './pages/admin/ErrorCorrectionReview';
import Message from './pages/Message';
import OrderDetail from './pages/OrderDetail';

const LayoutWithHeader = () => (
  <>
    <Header />
    <Outlet />
    <FloatingChatButton />
  </>
);

const LayoutWithoutHeader = () => (
  <>
    <Outlet />
    <FloatingChatButton />
  </>
);

function App() {
  // 应用启动时：清除旧的localStorage缓存，确保新logo生效
  useEffect(() => {
    // 清除旧的存储桶URL缓存
    localStorage.removeItem('site_logo_url');
    localStorage.removeItem('site_logo_version');
  }, []);

  return (
    <HashRouter>
      <Routes>
        <Route element={<LayoutWithHeader />}>
          <Route path="/" element={<Home />} />
          <Route path="/category/:categoryId" element={<CategoryPage />} />
          <Route path="/category/:categoryId/:subCategoryId/:levelId" element={<ExamListPage />} />
          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/favorites/review" element={<FavoritesReviewPage />} />
          <Route path="/wrong-questions" element={<WrongQuestionsPage />} />
          <Route path="/wrong-questions/review" element={<WrongQuestionsReviewPage />} />
          <Route path="/exam-records" element={<ExamRecordsPage />} />
          <Route path="/assignments" element={<AssignmentsPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/profile" element={<UserProfile />} />
          <Route path="/exam-categories" element={<ExamPlatform />} />
          <Route path="/payment-result" element={<PaymentResult />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/notices" element={<Notice />} />
          <Route path="/notice/:id" element={<NoticeDetail />} />
          <Route path="/message" element={<Message />} />
          <Route path="/downloads" element={<DownloadCenter />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/s/:code" element={<PayRedirect />} />
          <Route path="/point-center" element={<PointCenter />} />
          <Route path="/point-transactions" element={<PointTransactions />} />
          <Route path="/my-corrections" element={<MyCorrections />} />
          <Route path="/order/:orderId" element={<OrderDetail />} />
        </Route>

        <Route element={<LayoutWithoutHeader />}>
          <Route path="/python" element={<PythonPlayground />} />
          <Route path="/cpp" element={<CppPlayground />} />
          <Route path="/scratch" element={<ScratchEditorPage />} />
          <Route path="/quiz/:paperId" element={<Quiz />} />
          <Route path="/exam/:paperId" element={<Quiz />} />
          <Route path="/quiz/custom" element={<Quiz />} />
          <Route path="/exam-result/:paperId" element={<ExamResult />} />
        </Route>

        <Route path="/admin/*" element={<AdminLayout />}>
          <Route index element={<AdminPapers />} />
          <Route path="papers" element={<AdminPapers />} />
          <Route path="members" element={<AdminMembers />} />
          <Route path="admins" element={<AdminUsers />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="logs" element={<OperationLogs />} />
          <Route path="email-settings" element={<EmailSettings />} />
          <Route path="sms-settings" element={<SMSSettings />} />
          <Route path="paper-editor" element={<PaperEditor />} />
          <Route path="ai-settings" element={<AISettings />} />
          <Route path="downloads" element={<DownloadManager />} />
          <Route path="student-orders" element={<StudentOrders />} />
          <Route path="teacher-orders" element={<AdminTeacherOrders />} />
          <Route path="customer-service" element={<CustomerService />} />
          <Route path="message-config" element={<AdminMessageConfig />} />
          <Route path="message-test" element={<MessageTest />} />
          <Route path="message-templates" element={<MessageTemplates />} />
          <Route path="send-message" element={<SendMessage />} />
          <Route path="message-statistics" element={<MessageStatistics />} />
          <Route path="alipay-config" element={<AlipayConfig />} />
          <Route path="category-manager" element={<CategoryManager />} />
          <Route path="paper-upload" element={<PaperUpload />} />
          <Route path="pending-papers" element={<PendingPapers />} />
          <Route path="recycle-bin" element={<RecycleBin />} />
          <Route path="teachers" element={<Teachers />} />
          <Route path="notices" element={<AdminNotices />} />
          <Route path="nav-items" element={<NavItems />} />
          <Route path="footer-settings" element={<FooterSettings />} />
          <Route path="scratch-assets" element={<ScratchAssetsUpload />} />
          <Route path="answer-settings" element={<AnswerSettings />} />
          <Route path="homepage-editor" element={<HomepageEditor />} />
          <Route path="point-settings" element={<PointSettings />} />
          <Route path="error-correction-review" element={<ErrorCorrectionReview />} />
        </Route>

        <Route path="/teacher/*" element={<TeacherLayout />}>
          <Route index element={<AssignmentManage />} />
          <Route path="basic-info" element={<TeacherBasicInfo />} />
          <Route path="students" element={<TeacherStudentManagement />} />
          <Route path="assignments" element={<AssignmentManage />} />
          <Route path="assignment-publish" element={<AssignmentPublish />} />
          <Route path="assignments/publish" element={<AssignmentPublish />} />
          <Route path="assignments/grading/:assignmentId" element={<AssignmentGradingPage />} />
          <Route path="assignments/grading/:assignmentId/:studentId" element={<AssignmentGradingPage />} />
          <Route path="paper-review" element={<PaperReviewList />} />
          <Route path="paper-review/:paperId" element={<PaperReviewDetail />} />
          <Route path="orders" element={<TeacherOrders />} />
          <Route path="messages" element={<TeacherMessages />} />
          <Route path="gift-points" element={<TeacherGiftPoints />} />
        </Route>
      </Routes>
    </HashRouter>
  );
}

export default App;